import React, { useState } from 'react';
import { Unlink } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCostosPermissions } from '@/hooks/useCostosPermissions';
import { useDesasociarPresupuesto } from '../hooks/useDesasociarPresupuesto';
import { DesasociarPresupuestoDialog } from './DesasociarPresupuestoDialog';

interface BotonDesasociarPresupuestoProps {
  tramiteId: string;
  nombrePresupuesto?: string;
  /** Etiqueta de la escritura (por ejemplo, "#12"), para el tooltip y la confirmación. */
  escritura?: string;
  /** `icono`: botón chico junto al enlace a la escritura; `texto`: botón con etiqueta. */
  variante?: 'icono' | 'texto';
  disabled?: boolean;
}

/**
 * Botón que desasocia una escritura de su presupuesto importado, previa
 * confirmación. Quien lo renderiza decide si el usuario puede importar
 * (`presupuestos-proforma.import`).
 */
export const BotonDesasociarPresupuesto: React.FC<BotonDesasociarPresupuestoProps> = ({
  tramiteId,
  nombrePresupuesto,
  escritura,
  variante = 'texto',
  disabled = false,
}) => {
  const [abierto, setAbierto] = useState(false);
  const { canDelete } = useCostosPermissions();
  const { desasociar, isDesasociando } = useDesasociarPresupuesto();

  const handleConfirmar = async (eliminarItems: boolean) => {
    try {
      await desasociar({ tramiteId, eliminarItems });
      toast.success(
        eliminarItems
          ? 'Presupuesto desasociado. Se eliminaron los ítems de la escritura.'
          : 'Presupuesto desasociado. La escritura conserva sus ítems.'
      );
      setAbierto(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? 'No se pudo desasociar el presupuesto.');
    }
  };

  const etiqueta = escritura ? `Desasociar de la escritura ${escritura}` : 'Desasociar presupuesto';

  return (
    <>
      {variante === 'icono' ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="xs"
              aria-label={etiqueta}
              onClick={() => setAbierto(true)}
              disabled={disabled || isDesasociando}
            >
              <Unlink className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{etiqueta}</TooltipContent>
        </Tooltip>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAbierto(true)}
          disabled={disabled || isDesasociando}
        >
          <Unlink className="mr-2 h-4 w-4" />
          Desasociar
        </Button>
      )}

      <DesasociarPresupuestoDialog
        open={abierto}
        onOpenChange={setAbierto}
        nombrePresupuesto={nombrePresupuesto}
        escritura={escritura}
        puedeEliminarItems={canDelete}
        isLoading={isDesasociando}
        onConfirm={handleConfirmar}
      />
    </>
  );
};
